import { t, useLocale } from "@/i18n/locale";
import { Message } from "@/i18n/Message";
import { useState } from "react";
import WaitlistModal from "@/components/WaitlistModal";
import { PRO_WAITLIST_CTA, PRO_WAITLIST_NOTICE } from "@/components/billing/planPolicy";
import SurfaceState, { type SurfaceStateAction } from "./SurfaceState";

/**
 * The end of a public Try: one statement about what the visitor can do, one
 * primary action, and links for the rest. It reads intentionally like the
 * other surface states (launching/failure) so the surface never changes genre
 * when a Try runs out.
 */
export default function PublicTrialExpired({ seconds = 300, onBack, onAddToLibrary, adding = false, onRetry, retryAt = 0 }: {
  seconds?: number | null;
  onBack: () => void;
  onAddToLibrary?: () => void;
  adding?: boolean;
  /** Offered only while a fresh Try can actually start (the controller is idle
   *  and no rate-limit wait is pending) — display only, the state machine owns
   *  the real guard. */
  onRetry?: () => void;
  retryAt?: number;
}) {
  useLocale();
  const [open, setOpen] = useState(false);
  const [returnFocusTo, setReturnFocusTo] = useState<HTMLElement | null>(null);
  const actions: SurfaceStateAction[] = [];
  if (onAddToLibrary)
    actions.push({
      label: <Message message="Add your own copy to Library" />,
      kind: "primary",
      busy: adding,
      disabled: adding,
      onClick: onAddToLibrary,
    });
  if (onRetry)
    actions.push({
      label: <Message message="Start another Try" />,
      kind: onAddToLibrary ? "secondary" : "primary",
      disabled: retryAt > Date.now(),
      onClick: onRetry,
    });
  // With a real primary action on screen, browsing stays a quiet text link so
  // the surface keeps one primary + one secondary CTA. Alone, it is the action.
  const browseAsLink = Boolean(onAddToLibrary || onRetry);
  if (!browseAsLink)
    actions.push({
      label: <Message message="Browse apps" />,
      kind: "primary",
      onClick: onBack,
    });
  return <>
    <SurfaceState
      className="discovery-trial-expired"
      icon="clock"
      role="status"
      title={seconds && seconds % 60 === 0 ? t("Your {{value0}}-minute trial has ended", { value0: seconds / 60 }) : t("Your public trial has ended")}
      description={onAddToLibrary
        ? <Message message="Save to Library makes your own copy of this app. Work in this Try is not carried over." />
        : <Message message="This Try has ended. Starting again creates a new Try, subject to the usual limits." />}
      actions={actions}
      note={<>
        {browseAsLink ? <button type="button" className="surface-state-link" onClick={onBack}><Message message="Browse apps" /></button> : null}
        <Message message="Longer public app trials are planned for Pro." />{" "}
        <button type="button" className="surface-state-link" onClick={event => {
          setReturnFocusTo(event.currentTarget);
          setOpen(true);
        }} title={t(PRO_WAITLIST_NOTICE)}>{t(PRO_WAITLIST_CTA)}</button>
      </>}
    />
    <WaitlistModal open={open} onClose={() => setOpen(false)} product="cloud"
      source="pwa_home" returnFocusTo={returnFocusTo} />
  </>;
}
